import _ from "lodash";
import { Assessment, Course, CourseChangeRequest, CourseValidationError, Lesson, MultipleChoiceQuestion, Option, Question, Skill, TextQuestion, Section, PodcastContent, SimulationLesson } from "src/types/coaching/courses";

export const prepareCourseRequest = (source: Course, target: Course) => {
  const changes = {} as CourseChangeRequest;

  if (!_.isEqual(source.name, target.name) && target.name) {
    changes.name = target.name;
  }

  if (
    !_.isEqual(source.passPercentage, target.passPercentage) &&
    target.passPercentage != null
  ) {
    changes.passPercentage = target.passPercentage;
  }

  if (!_.isEqual(source.description, target.description) && target.description) {
    changes.description = target.description;
  }

  if (!_.isEqual(source.banner, target.banner)) {
    changes.banner = { ...target.banner } as any;
    const sourceBackground = _.get(source, 'banner.background.id');
    const targetBackground = _.get(target, 'banner.background.id');

    if (sourceBackground !== targetBackground) {
      _.set(changes, 'banner.background', targetBackground);
    } else {
      _.set(changes, 'banner.background', null);
    }
  }

  const sourceContents = flattenContents(source.contents || []);
  const targetContents = flattenContents(target.contents || []);
  
  const contents = prepareContentsRequest(sourceContents, targetContents);
  if (_.size(contents) > 0) {
    changes.contents = contents;
  }

  if (_.size(sourceContents) > 0) {
    const deletedContents = sourceContents?.filter(content => {
      const found = targetContents?.find(item => {
        return content.id == item.id;
      });

      return found == null;
    }).map(item => item.id).filter(item => item != null);

    if (_.size(deletedContents) > 0) {
      changes.deletedContents = deletedContents;
    }
  }

  const sourceSkills = (source.skills || []).map(item => item.id!);
  const targetSkills = (target.skills || []).map(item => item.id!);
  if (!_.isEqual(sourceSkills, targetSkills)) {
    changes.skills = targetSkills;
  }

  return changes;
}

const prepareContentsRequest = (
  sourceContents: any[],
  targetContents: any[],
) => {
  return targetContents
    .map((content) => {
      const sourceContent = sourceContents.find(
        (item) => item.id != null && item.id == content.id,
      );
      if (content.entityType === "lesson") {
        return prepareLessonRequest(content, sourceContent);
      } else if (content.entityType === "assessment") {
        return prepareAssessmentRequest(content, sourceContent);
      } else if (content.entityType === "section") {
        return prepareSectionRequest(content, sourceContent);
      } else if (
        content.entityType === "podcast" ||
        content.entityType === "simulation"
      ) {
        return prepareContentRequest(content, sourceContent);
      }
    })
    .filter((content) => content != null);
};

const prepareContentRequest = (content: any, sourceContent: any | null) => {
  const changes: any = {};
  if (!_.isEqual(sourceContent?.name, content.name) && content.name) {
    changes.name = content.name;
  }
  if (!_.isEqual(sourceContent?.description, content.description)) {
    changes.description = content.description;
  }
  if (!_.isEqual(sourceContent?.order, content.order)) {
    changes.order = content.order;
  }
  if (!_.isEqual(sourceContent?.parent, content.parent)) {
    changes.parent = content.parent;
  }
  if (!_.isEqual(sourceContent?.asset?.id, content?.content?.id)) {
    changes.content = content.content?.id;
  }
  if (!_.isEqual(sourceContent?.banner, content.banner)) {
    changes.banner = content.banner;
  }
  if (_.isEmpty(changes)) {
    return null;
  }
  return {
    id: content.id,
    uuid: content.uuid,
    entityType: content.entityType,
    draft: true,
    ...changes,
  };
};

export const flattenContents = (contents: any[]) => {
  return contents.map((item) => {
    if (item.entityType === 'section') {
      let sectionContents = (item.contents || []).map((content) => {
        return { ...content, parent: item.uuid };
      });
      
      sectionContents = flattenContents(sectionContents);
      return [item, ...sectionContents];
    }

    return item;
  }).flat();
};

const prepareLessonRequest = (content: Lesson, sourceContent: Lesson | null) => {
  const changes = {} as any;

  if (!_.isEqual(sourceContent?.name, content.name) && content.name) {
    changes.name = content.name;
  }

  if (!_.isEqual(sourceContent?.description, content.description)) {
    changes.description = content.description;
  }

  if (!_.isEqual(sourceContent?.order, content.order)) {
    changes.order = content.order;
  }

  if (!_.isEqual(sourceContent?.parent, content.parent)) {
    changes.parent = content.parent;
  }

  if (!_.isEqual(sourceContent?.content?.id, content?.content?.id)) {
    changes.content = content.content.id;
  }

  if (_.keys(changes).length == 0) {
    return null;
  }

  const data = {
    id: content.id,
    uuid: content.uuid,
    entityType: content.entityType,
    draft: true,
    ...changes
  }

  return data;
}

const prepareAssessmentRequest = (content: Assessment, sourceContent: Assessment) => {
  const changes = {} as any;

  if (!_.isEqual(sourceContent?.name, content.name)) {
    changes.name = content.name;
  }

  if (!_.isEqual(sourceContent?.description, content.description)) {
    changes.description = content.name;
  }

  if (!_.isEqual(sourceContent?.order, content.order)) {
    changes.order = content.order;
  }

  if (!_.isEqual(sourceContent?.parent, content.parent)) {
    changes.parent = content.parent;
  }

  const questions = prepareQuestionsRequest(content.questions, sourceContent?.questions);
  if (_.size(questions) > 0) {
    changes.questions = questions;
  }

  if (_.size(sourceContent?.questions) > 0) {
    const deletedQuestions = sourceContent?.questions?.filter(question => {
      const found = content.questions.find(item => {
        return question.id == item.id;
      });

      return found == null;
    }).map(item => item.id).filter(item => item != null);

    if (_.size(deletedQuestions) > 0) {
      changes.deletedQuestions = deletedQuestions;
    }
  }

  if (_.keys(changes).length == 0) {
    return null;
  }

  const data = {
    id: content.id,
    uuid: content.uuid,
    entityType: content.entityType,
    draft: true,
    ...changes
  }

  return data;
}

const prepareSectionRequest = (content: Section, sourceContent: Section | null) => {
  const changes = {} as any;

  if (!_.isEqual(sourceContent?.name, content.name) && content.name) {
    changes.name = content.name;
  }

  if (!_.isEqual(sourceContent?.description, content.description)) {
    changes.description = content.description;
  }

  if (!_.isEqual(sourceContent?.order, content.order)) {
    changes.order = content.order;
  }

  if (_.keys(changes).length == 0) {
    return null;
  }

  const data = {
    id: content.id,
    uuid: content.uuid,
    entityType: content.entityType,
    draft: true,
    ...changes
  }

  return data;
}

const prepareQuestionsRequest = (questions: Question[], sourceQuestions: Question[] | null) => {
  return questions.map((question) => {
    const sourceQuestion = sourceQuestions?.find(item => item.id == question.id);

    if (question.type === 'multiple_choice' || question.type === 'true_false') {
      return prepareMultipleChoiceQuestionRequest(question, sourceQuestion);
    } else if (question.type === 'text') {
      return prepareTextQuestionRequest(question, sourceQuestion);
    }
  }).filter(question => question != null);
}

const prepareMultipleChoiceQuestionRequest = (question: Question, sourceQuestion?: Question | null) => {
  const multipleChoiceQuestion = question as MultipleChoiceQuestion;
  const sourceMultipleChoiceQuestion = sourceQuestion as MultipleChoiceQuestion;

  const changes = {} as any;

  if (!_.isEqual(sourceQuestion?.name, question.name)) {
    changes.name = question.name;
  }

  if (!_.isEqual(sourceQuestion?.order, question.order)) {
    changes.order = question.order;
  }

  if (!_.isEqual(sourceMultipleChoiceQuestion?.answer, multipleChoiceQuestion.answer)) {
    changes.answer = multipleChoiceQuestion.answer;
  }

  const sourceOptions = prepareMultipleChoiceOptionsRequest(sourceMultipleChoiceQuestion?.options || []);
  const targetOptions = prepareMultipleChoiceOptionsRequest(multipleChoiceQuestion.options || []);

  if (!_.isEqual(sourceOptions, targetOptions)) {
    changes.options = targetOptions;
  }

  if (_.keys(changes).length == 0) {
    return null;
  }

  const data = {
    id: question.id,
    uuid: question.uuid,
    type: question.type,
    draft: true,
    ...changes
  }

  return data;
}

const prepareMultipleChoiceOptionsRequest = (options: Option[]) => {
  return options?.map((option, index) => {
    return {
      uuid: option.uuid,
      name: option.name,
      order: index
    }
  });
}

const prepareTextQuestionRequest = (question: Question, sourceQuestion?: Question | null) => {
  const textQuestion = question as TextQuestion;
  const sourceTextQuestion = sourceQuestion as TextQuestion;

  const changes = {} as any;

  if (!_.isEqual(sourceQuestion?.name, question.name)) {
    changes.name = question.name;
  }

  if (!_.isEqual(sourceQuestion?.order, question.order)) {
    changes.order = question.order;
  }

  if (!_.isEqual(sourceTextQuestion?.answer, textQuestion.answer)) {
    changes.answer = textQuestion.answer;
  }

  if (_.keys(changes).length == 0) {
    return null;
  }

  const data = {
    id: question.id,
    uuid: question.uuid,
    type: question.type,
    draft: true,
    ...changes
  }

  return data;
}

//Validate Course
export const validateCourseRequest = (course: Course) => {
  const errors: CourseValidationError[] = [];

  if (!course.name || course.name.trim().length === 0) {
    errors.push({
      type: 'course',
      uuid: course.id!,
      property: 'name',
      message: 'Course name is required',
      warning: false
    });
  } else if (course.name.length > 200) {
    errors.push({
      type: 'course',
      uuid: course.id!,
      property: 'name',
      message: 'Course name cannot be more than 200 characters',
      warning: false
    });
  }

  if (!course.description) {
    errors.push({
      type: 'course',
      uuid: course.id!,
      property: 'description',
      message: 'While course descriptions are optional, we highly recommend you to enter relevant information to help your learners understand the course better.',
      warning: true
    });
  } else if (course.description.length > 1000) {
    errors.push({
      type: 'course',
      uuid: course.id!,
      property: 'description',
      message: 'Description cannot be greater that 1000 characters',
      warning: false
    });
  }

  if (!course.skills || _.size(course.skills) == 0) {
    errors.push({
      type: 'course',
      uuid: course.id!,
      property: 'skills',
      message: 'Enter skills relevant to this course',
      warning: false
    });
  }

  if (!course.contents || _.size(course.contents) == 0) {
    errors.push({
      type: 'course',
      uuid: course.id!,
      property: 'contents',
      message: 'Add atleast one or more lesson/assessment to the course',
      warning: false
    });
  }

  const contents = course.contents || [];
  contents.forEach((content) => {
    if (content.entityType === 'lesson') {
      errors.push(...validateLesson(content));
    } else if (content.entityType === 'assessment') {
      errors.push(...validateAssessment(content));
    } else if (content.entityType === 'section') {
      errors.push(...validateSection(content));
    } else if (content.entityType === 'simulation') {
      errors.push(...validateSimulation(content));
    } else if (content.entityType === 'podcast') {
      errors.push(...validatePodcast(content));
    }
  });


  return errors;
}

const validateLesson = (lesson: Lesson) => {
  const errors: CourseValidationError[] = [];

  if (!lesson.name) {
    errors.push({
      type: 'lesson',
      uuid: lesson.uuid!,
      property: 'name',
      message: 'Lesson name is required',
      warning: false
    });
  }

  if (_.size(lesson.description) > 1000) {
    errors.push({
      type: 'lesson',
      uuid: lesson.uuid!,
      property: 'name',
      message: 'Decription cannot be greater than 1000 characters',
      warning: false
    });
  }

  if (lesson.content == null) {
    errors.push({
      type: 'lesson',
      uuid: lesson.uuid!,
      property: 'asset',
      message: 'Asset was deleted or not found',
      warning: false
    });
  }

  return errors;
}

const validatePodcast = (podcast: PodcastContent) => {
  const errors: CourseValidationError[] = [];

  if (podcast.content == null) {
    errors.push({
      type: 'podcast',
      uuid: podcast.uuid!,
      property: 'asset',
      message: 'Podcast file was deleted or not found',
      warning: false
    });
  }

  return errors;
};

const validateSimulation = (simulation: SimulationLesson) => {
  const errors: CourseValidationError[] = [];

  if (simulation.content == null) {
    errors.push({
      type: 'simulation',
      uuid: simulation.uuid!,
      property: 'asset',
      message: 'Simulation file was deleted or not found',
      warning: false
    });
  }

  return errors;
};

const validateAssessment = (assessment: Assessment) => {
  const errors: CourseValidationError[] = [];

  if (!assessment.name) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: 'name',
      message: 'Assessment name is required',
      warning: false
    });
  } else if (_.size(assessment.name) > 200) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: 'name',
      message: 'Name cannot be greater than 200 characters',
      warning: false
    });
  }

  if (!assessment.questions || _.size(assessment.questions) == 0) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: 'questions',
      message: 'An assessment should have atleast one question',
      warning: false
    });
  }

  const questions = assessment.questions || [];
  questions.forEach((question, index) => {
    if (question.type === 'multiple_choice' || question.type === 'true_false') {
      errors.push(...validateMultipleChoiceQuestion(assessment, question, index));
    } else if (question.type === 'text') {
      errors.push(...validateTextQuestion(assessment, question, index));
    }
  });

  return errors;
}

const validateSection = (section: Section) => {
  const errors: CourseValidationError[] = [];

  if(section.name == null || section.name.trim().length === 0) {
    errors.push({
      type: 'section',
      uuid: section.uuid!,
      property: 'name',
      message: 'Section name is required',
      warning: false,
    });
  }else if (section.name.length > 200) {
    errors.push({
      type: 'section',
      uuid: section.uuid!,
      property: 'name',
      message: 'Section name cannot be more than 200 characters',
      warning: false
    });
  }

  if (!section.contents || _.size(section.contents) === 0) {
    errors.push({
      type: 'section',
      uuid: section.uuid!,
      property: 'contents',
      message: 'A section should have at least one lesson/assessment',
      warning: false,
    });
  }else if (section.description.length > 1000) {
    errors.push({
      type: 'section',
      uuid: section.uuid!,
      property: 'description',
      message: 'Description cannot be greater that 1000 characters',
      warning: false
    });
  }

  return errors;
};

const validateMultipleChoiceQuestion = (assessment: Assessment, question: MultipleChoiceQuestion, index: number) => {
  const errors: CourseValidationError[] = [];

  if (!question.name) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.name`,
      message: `Question cannot be empty`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  } else if (_.size(question.name) > 200) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.name`,
      message: `Question title cannot be greater than 200`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  }

  if (!question.answer) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.answer`,
      message: `Enter a valid answer`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  }

  if (!question.options || _.size(question.options) < 2) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.options`,
      message: `Two or more choices are required`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  }

  question.options?.forEach((option, index) => {
    if (!option.name) {
      errors.push({
        type: 'assessment',
        uuid: assessment.uuid!,
        property: `questions.${question.uuid}.options.${option.uuid}.name`,
        message: `Choice ${index + 1} cannot be empty`,
        additionalData: {
          question: question.uuid,
          option: option.uuid,
        },
        warning: false
      });
    } else if (_.size(option.name) > 200) {
      errors.push({
        type: 'assessment',
        uuid: assessment.uuid!,
        property: `questions.${question.uuid}.options.${option.uuid}.name`,
        message: `Choice ${index + 1} cannot be greater than 200 characters`,
        additionalData: {
          question: question.uuid,
          option: option.uuid,
        },
        warning: false
      });
    }
  });

  return errors;
}

const validateTextQuestion = (assessment: Assessment, question: TextQuestion, index: number) => {
  const errors: CourseValidationError[] = [];

  if (!question.name) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.name`,
      message: `Question cannot be empty`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  } else if (_.size(question.name) > 200) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.name`,
      message: `Question title cannot be greater than 200`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  }

  if (!question.answer) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.answer`,
      message: `Answer is mandatory`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  } else if (_.size(question.answer) > 1000) {
    errors.push({
      type: 'assessment',
      uuid: assessment.uuid!,
      property: `questions.${question.uuid}.answer`,
      message: `Answer cannot be greater than 1000 characters`,
      additionalData: {
        question: question.uuid
      },
      warning: false
    });
  }

  return errors;
}

export const updateCoursePreferences = (preferences: any) => {
  const json = JSON.stringify(preferences);
  localStorage.setItem('pf_course_preferences', json);
}

export const getCoursePreferences = () => {
  const json = localStorage.getItem('pf_course_preferences');
  return json ? JSON.parse(json) : {};
}

export const processSkills = (skills: Skill[], selected: Skill[]) => {
  let changes = [...selected];

  const parentMap = new Map();
  selected.map(item => {
    if (item.parent) {
      let children = parentMap.get(item.parent);
      if (!children) {
        children = [];
        parentMap.set(item.parent, children);
      }

      children = [...children, item.id]
      parentMap.set(item.parent, children);
    }
  });

  parentMap.forEach((children, parent) => {
    const skill = skills.find(item => item.id === parent);
    if (skill && _.size(skill.children) > 0) {
      const filtered = skill.children.filter(child => !_.includes(children, child.id));

      if (_.size(filtered) == 0) {
        changes = changes.filter(item => !_.includes(children, item.id))
        changes = [...changes, skill];
      }
    }
  });

  return changes;
}

export const getIndexForContent = (contents: any[], content: string, parent: string | null) => {
  let contentsToSearch = contents || [];
  if (parent) {
    const section = _.find(contentsToSearch, (c) => c.uuid === parent);
    contentsToSearch = _.get(section, "children") || [];
  }

  if (_.size(contentsToSearch) === 0) {
    return 1;
  }

  contentsToSearch = contentsToSearch.filter(c => ["lesson", "section", "assessment", "podcast", "simulation"].includes(c.entityType));

  const contentIndex = _.findIndex(contentsToSearch, (c) => c.uuid === content);

  if (contentIndex === -1) {
    return 1;
  }

  return contentIndex + 1;
}  